import {
  DocumentArrowDownIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { downloadDealDocuments } from '../../services/dealDocuments';
import { formatDealType, normalizeDealType } from '../../utils/dealType';

interface Deal {
  id: string;
  deal_number: string;
  deal_type: string;
  deal_status: string;
  customer_id?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  year?: number;
  make?: string;
  model?: string;
  stock_number?: string;
  purchase_price?: number;
  created_at: string;
  primary_image?: string;
}

const DealsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFilter = searchParams.get('customer_id');
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dealTypeFilter, setDealTypeFilter] = useState('');
  const [dealStatusFilter, setDealStatusFilter] = useState('');

  const downloadDocument = async (dealId: string, type: 'bill_of_sale' | 'tila') => {
    try {
      await downloadDealDocuments(dealId, type);
    } catch (e) {
      console.error('PDF generation failed', e);
      toast.error(
        `Failed to generate ${type.replace('_', ' ')}. Make sure the deal has been finalized.`
      );
    }
  };

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Deal[]>('/deals/', {
        params: customerIdFilter ? { customer_id: customerIdFilter } : undefined,
      });
      // The deals list now carries the linked vehicle's primary_image directly,
      // so no per-row image lookup is needed.
      setDeals(response || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [customerIdFilter]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const getVehicleThumbnail = (imageUrl?: string) => {
    if (!imageUrl) {
      return (
        <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
          <PhotoIcon className="h-4 w-4 text-gray-400" />
        </div>
      );
    }

    return (
      <img
        src={apiService.getFileUrl(imageUrl)}
        alt="Vehicle"
        className="w-10 h-10 object-cover rounded border"
        onError={(e) => {
          // Fallback to placeholder on error
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML =
              '<div class="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center"><svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
          }
        }}
      />
    );
  };
  const getDealNumber = (dealNumber: string) => {
    // If it's already just a number, return it
    if (/^\d+$/.test(dealNumber)) {
      return dealNumber;
    }

    // If it's in the format "DEAL-customer_XXX-vehicle_YYY", extract XXX
    const match = dealNumber.match(/DEAL-customer_(\d+)-vehicle/);
    if (match) {
      return match[1];
    }

    // Return as-is if we can't parse it
    return dealNumber;
  };

  const filteredDeals = deals.filter((deal) => {
    const matchesSearch =
      !searchTerm ||
      getDealNumber(deal.deal_number).toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${deal.customer_first_name} ${deal.customer_last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      `${deal.year} ${deal.make} ${deal.model}`.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      !dealTypeFilter || normalizeDealType(deal.deal_type) === normalizeDealType(dealTypeFilter);
    const matchesStatus = !dealStatusFilter || deal.deal_status === dealStatusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDealTypeBadge = (type: string) => {
    const colors = {
      CASH: 'bg-green-100 text-green-800',
      OUTSIDE_FINANCE: 'bg-blue-100 text-blue-800',
      BHPH: 'bg-purple-100 text-purple-800',
    };
    return colors[normalizeDealType(type) as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getDealStatusBadge = (status: string) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      SOLD: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Deals</h1>
          <p className="mt-2 text-sm text-gray-700">
            {customerIdFilter
              ? 'Manage deals for the selected customer.'
              : 'Manage dealership transactions and track deal performance.'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button className="btn-primary" onClick={() => navigate('/calculator')}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Deal
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="form-input pl-10"
                placeholder="Search deals by number, customer, or vehicle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="form-input"
              value={dealTypeFilter}
              onChange={(e) => setDealTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="CASH">Cash</option>
              <option value="FINANCE">Finance</option>
              <option value="BHPH">BHPH</option>
            </select>
            <select
              className="form-input"
              value={dealStatusFilter}
              onChange={(e) => setDealStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="SOLD">Sold</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Deals list */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading deals...</p>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No deals found</h3>
            <p className="text-gray-500 mb-6">
              {deals.length === 0
                ? 'Get started by adding your first deal to the system.'
                : 'Try adjusting your search or filters.'}
            </p>
            {deals.length === 0 && (
              <button className="btn-primary" onClick={() => navigate('/calculator')}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Deal
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deal #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Year | Make | Model
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deal Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purchase Price
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredDeals.map((deal) => (
                  <tr key={deal.id} className="table-row">
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">
                      {getDealNumber(deal.deal_number)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {deal.customer_first_name && deal.customer_last_name ? (
                        deal.customer_id ? (
                          <Link
                            to={`/customers/${deal.customer_id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {deal.customer_first_name} {deal.customer_last_name}
                          </Link>
                        ) : (
                          `${deal.customer_first_name} ${deal.customer_last_name}`
                        )
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 w-48">
                      <div className="flex items-center space-x-2">
                        <div>
                          {deal.year && deal.make && deal.model
                            ? `${deal.year} ${deal.make} ${deal.model}`
                            : 'N/A'}
                          {deal.stock_number && (
                            <div className="text-xs text-gray-500">#{deal.stock_number}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-sm w-16">
                      <div className="flex justify-center">
                        {getVehicleThumbnail(deal.primary_image)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDealTypeBadge(deal.deal_type)}`}
                      >
                        {formatDealType(deal.deal_type)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDealStatusBadge(deal.deal_status)}`}
                      >
                        {deal.deal_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatCurrency(deal.purchase_price)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {formatDate(deal.created_at)}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center space-x-1">
                        <Link
                          to={`/deals/${deal.id}`}
                          className="text-blue-600 hover:text-blue-800 p-0.5"
                          title="View Deal"
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          className="text-green-600 hover:text-green-800 p-0.5"
                          title="Download Bill of Sale"
                          onClick={() => downloadDocument(deal.id, 'bill_of_sale')}
                        >
                          <DocumentArrowDownIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="text-purple-600 hover:text-purple-800 p-0.5"
                          title="Download TILA Disclosure"
                          onClick={() => downloadDocument(deal.id, 'tila')}
                        >
                          <DocumentTextIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="text-gray-400 hover:text-gray-600 p-0.5"
                          title="Edit Deal"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="text-gray-400 hover:text-red-600 p-0.5"
                          title="Delete Deal"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealsPage;
